'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { Card } from '@/components/cms/Card';
import { Icon } from '@/components/ui';
import clsx from 'clsx';
import { useHeroSliderPromotionTracking } from '@/hooks/useHeroSliderPromotionTracking';
import { useSlideNavigation } from '../HeroSlider.hooks';
import styles from '../HeroSlider.module.scss';
import type { BaseHeroSliderProps } from '../HeroSlider.types';

export const StandardHeroSlider = ({
  slides,
  autoplay = false,
  interval = 5000,
  fixToTop = false,
  preload,
  cmsIndex,
}: BaseHeroSliderProps) => {
  const aria = useAriaLabels();
  const testIdPrefix = cmsIndex !== undefined ? `heroslider-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  const { activeIndex, hasMultipleSlides, setIsPaused, goToSlide, goToPrev, goToNext } =
    useSlideNavigation(slides.length, autoplay, interval);
  const setContainerRef = useHeroSliderPromotionTracking({ slides, activeIndex });

  if (slides.length === 0) {
    return null;
  }

  return (
    <section
      ref={setContainerRef}
      className={clsx(styles.slider, fixToTop && styles.fixedTop)}
      data-testid={testId('section')}
    >
      <div
        role="group"
        className={styles.slides}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.sys?.id || index}
            className={clsx(styles.slide, index === activeIndex && styles.active)}
            aria-hidden={index !== activeIndex}
          >
            <Card
              type="HeroBanner"
              data={slide}
              preload={preload && index === 0}
              cmsIndex={cmsIndex !== undefined ? cmsIndex * 100 + index : undefined}
              promotionSlot={index}
              trackPromotionView={false}
            />
          </div>
        ))}
      </div>

      {hasMultipleSlides && (
        <>
          <div className={styles.arrows}>
            <button
              type="button"
              className={styles.arrowButton}
              onClick={goToPrev}
              aria-label={aria.action('previousSlide')}
              data-testid={testId('prev')}
            >
              <Icon name="chevron-left" size={24} />
            </button>
            <button
              type="button"
              className={styles.arrowButton}
              onClick={goToNext}
              aria-label={aria.action('nextSlide')}
              data-testid={testId('next')}
            >
              <Icon name="chevron-right" size={24} />
            </button>
          </div>

          <div className={styles.dots} data-testid={testId('pagination')}>
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                className={clsx(styles.dot, index === activeIndex && styles.active)}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : 'false'}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

StandardHeroSlider.displayName = 'StandardHeroSlider';
